/* eslint-disable react/no-did-update-set-state */
import * as React from 'react';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { connect } from 'react-redux';

import Spinner from 'components/spinner/Spinner';

import CampaignReportTabs from '../campaign-report-tabs/CampaignReportTabs';
import InstagramPostsReporting from '../instagram-posts-reporting/InstagramPostsReporting';
import InstagramStoryReporting from '../instagram-story-reporting/InstagramStoryReporting';
import InstagramReelReporting from '../instagram-reel-reporting/InstagramReelReporting';
import TikTokVideoReporting from '../tiktok-video-reporting/TiktokVideoReporting';
import ZalandoOutfitReporting from '../zalando-outfit-reporting/ZalandoOutfitReporting';

import { fetchReportingAudienceData, resetReportingAudienceData } from '../../reducers/reporting-audience-data';

import { getSupportedTaskTypes } from 'utils/reporting';
import { __t, applyTranslations } from 'utils/language';
import { isInfluencerRealm } from 'utils/account';
import {
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  DELIVERABLE_TYPE_ZALANDO_OUTFIT
} from 'utils/deliverables';
import { trackPageChanged } from 'modules/tracking/utils/tracking';

import type { Campaign, ContentCardCreatorProfile } from 'types';
import type { CreatorPerformance, CreatorPerformanceReportingState } from '../../types/CreatorPerformance.type';
import type {
  ReportingDeliverableTypes,
  ReportingMetaInfoIGStorySnippetType,
  ReportingSummaryInfluencersMap
} from 'modules/reporting/types';

applyTranslations(require('./creator-performance-by-channel.i18n.json'));

import './creator-performance-by-channel.scss';

type OwnProps = {
  campaign: Campaign,
  creator: ContentCardCreatorProfile,
  creatorPerformance: CreatorPerformance,
  igStorySnippetType: ReportingMetaInfoIGStorySnippetType,
  isLoading: boolean
}

type StateProps = {
  reportingAudienceData: CreatorPerformanceReportingState,
  isSCP: boolean
};

type DispatchProps = {
  dispatchFetchReportingAudienceData: (campaignId: number, creatorId: number, filter: ReportingDeliverableTypes) => void,
  dispatchResetReportingAudienceData: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  filterByPostType?: ReportingDeliverableTypes
};

class CreatorPerformanceByChannel extends React.Component<Props, State> {
  state: State = {
    filterByPostType: 'instagram_post'
  };

  componentDidMount() {
    if (!this.state.filterByPostType) {
      return;
    }

    this.visitVirtualPage(this.state.filterByPostType);
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { filterByPostType } = this.state;

    if (filterByPostType !== prevState.filterByPostType) {
      this.setState(state => ({
        filterByPostType: state.filterByPostType
      }), this.loadAudienceData);
    }

    if (prevProps.creatorPerformance !== this.props.creatorPerformance) {
      this.setState({
        filterByPostType: this.getActualTaskTypes()[0] || this.getAvailableTaskTypes()[0]
      }, this.loadAudienceData);
    }
  }

  componentWillUnmount() {
    const { dispatchResetReportingAudienceData } = this.props;
    dispatchResetReportingAudienceData();
  }

  visitVirtualPage(filterByPostType: ReportingDeliverableTypes): void {
    const tabDictionary: { [k in ReportingDeliverableTypes]: string } = {
      instagram_post: 'instagram-posts',
      instagram_story: 'instagram-stories',
      instagram_reel: 'instagram-reels',
      tiktok_video: 'tiktok-videos',
      youtube_video: 'youtube-videos',
      zalando_outfit: 'zalando-outfits'
    };

    trackPageChanged({ url: `${window.location.pathname}/${tabDictionary[filterByPostType]}` });
  }

  handleFilterChanged = (filterByPostType: ReportingDeliverableTypes): void => {
    this.setState({
      filterByPostType
    });
    this.visitVirtualPage(filterByPostType);
  };

  loadAudienceData = () => {
    const { campaign, creator, dispatchFetchReportingAudienceData } = this.props;
    const { filterByPostType } = this.state;

    if (filterByPostType) {
      dispatchFetchReportingAudienceData(campaign.id, creator.id, filterByPostType);
    }
  };

  getActualTaskTypes(): Array<ReportingDeliverableTypes> {
    const { creatorPerformance, isSCP } = this.props;

    return getSupportedTaskTypes(isSCP)
      .filter(type => creatorPerformance.actualDeliverableTypes?.includes(type));
  }

  getAvailableTaskTypes(): Array<ReportingDeliverableTypes> {
    const { creatorPerformance, isSCP } = this.props;
    return getSupportedTaskTypes(isSCP)
      .filter(type => creatorPerformance.agreedDeliverableTypes?.includes(type));
  }

  renderHeader() {
    const { creatorPerformance } = this.props;
    const availableTasks = this.getAvailableTaskTypes();
    const hasData = !isEmpty(creatorPerformance.typedReports);

    if (!this.state.filterByPostType || !hasData || availableTasks.length < 2) {
      return null;
    }

    return (
      <React.Fragment>
        <div className="creator-performance-by-channel__title">
          {__t('creator_performance_by_channel.title')}
        </div>
        <CampaignReportTabs
          activeTab={this.state.filterByPostType}
          enabledTabs={this.getActualTaskTypes()}
          availableTabs={this.getAvailableTaskTypes()}
          onClick={this.handleFilterChanged}
        />
      </React.Fragment>
    );
  }

  renderSpinner(): JSX.Element {
    return (
      <div className="creator-performance-by-channel__spinner">
        <Spinner
          position="inline"
          size="small"
          color="light"
        />
      </div>
    );
  }

  renderInstagramPosts(influencersMapById: ReportingSummaryInfluencersMap): React.ReactNode {
    const { reportingAudienceData, campaign, creatorPerformance } = this.props;
    const hideInfo = this.getAvailableTaskTypes().length === 1;
    const instagramPostsReporting = creatorPerformance.typedReports?.instagramPost;
    const hasData = !isEmpty(instagramPostsReporting);

    if (!reportingAudienceData || !hasData) {
      return null;
    }

    return (
      <div className="creator-performance-by-channel__report">
        <InstagramPostsReporting
          hideTitle
          hideCreators
          hideInfo={hideInfo}
          campaign={campaign}
          instagramPostsReporting={creatorPerformance.typedReports?.instagramPost}
          isLoading={false}
          audienceData={{
            isLoading: reportingAudienceData.isLoading,
            data: reportingAudienceData.audienceData?.instagramPost
          }}
          hasDefinedAttribution={!creatorPerformance.isBudgetSplitOutdated}
          influencersMapById={influencersMapById}
        />
      </div>
    );
  }

  renderInstagramStories(influencersMapById: ReportingSummaryInfluencersMap): React.ReactNode {
    const { reportingAudienceData, campaign, creatorPerformance, igStorySnippetType } = this.props;
    const hideInfo = this.getAvailableTaskTypes().length === 1;
    const storiesReporting = creatorPerformance.typedReports?.instagramStory;
    const hasData = !isEmpty(storiesReporting);

    if (!reportingAudienceData || !hasData) {
      return null;
    }

    return (
      <div className="creator-performance-by-channel__report">
        <InstagramStoryReporting
          hideTitle
          hideCreators
          hideInfo={hideInfo}
          campaign={campaign}
          instagramStoryReporting={creatorPerformance.typedReports?.instagramStory}
          igStorySnippetType={igStorySnippetType}
          isLoading={false}
          audienceData={{
            isLoading: reportingAudienceData.isLoading,
            data: reportingAudienceData.audienceData?.instagramStory
          }}
          hasDefinedAttribution={!creatorPerformance.isBudgetSplitOutdated}
          influencersMapById={influencersMapById}
        />
      </div>
    );
  }

  renderInstagramReels(influencersMapById: ReportingSummaryInfluencersMap): React.ReactNode {
    const { reportingAudienceData, campaign, creatorPerformance } = this.props;
    const hideInfo = this.getAvailableTaskTypes().length === 1;
    const reelsReporting = creatorPerformance.typedReports?.instagramReel;
    const hasData = !isEmpty(reelsReporting);

    if (!reportingAudienceData || !hasData) {
      return null;
    }

    return (
      <div className="creator-performance-by-channel__report">
        <InstagramReelReporting
          hideTitle
          hideCreators
          hideInfo={hideInfo}
          campaign={campaign}
          instagramReelReporting={creatorPerformance.typedReports?.instagramReel}
          isLoading={false}
          audienceData={{
            isLoading: reportingAudienceData.isLoading,
            data: reportingAudienceData.audienceData?.instagramReel
          }}
          hasDefinedAttribution={!creatorPerformance.isBudgetSplitOutdated}
          influencersMapById={influencersMapById}
        />
      </div>
    );
  }

  renderZalandoOutfit(): React.ReactNode {
    const { creatorPerformance } = this.props;
    const outfitsProgress = creatorPerformance?.summary?.outfitsProgress ?? null;
    const zalandoOutfit = creatorPerformance?.summary?.tasksProgress?.zalandoOutfit ?? null;
    const classNames = classnames('creator-performance-by-channel__report', {
      'creator-performance-by-channel__report--outfit': true
    });

    if (outfitsProgress === null || zalandoOutfit === null) {
      return null;
    }

    return (
      <div className={classNames}>
        <ZalandoOutfitReporting
          isLoading={false}
          availableTaskTypes={[DELIVERABLE_TYPE_ZALANDO_OUTFIT]}
          outfitsProgress={outfitsProgress}
          zalandoOutfit={zalandoOutfit}
        />
      </div>
    );
  }

  renderTiktokVideos(influencersMapById: ReportingSummaryInfluencersMap): React.ReactNode {
    const { reportingAudienceData, campaign, creatorPerformance } = this.props;
    const hideInfo = this.getAvailableTaskTypes().length === 1;
    const videosReporting = creatorPerformance.typedReports?.tiktokVideo;
    const hasData = !isEmpty(videosReporting);

    if (!reportingAudienceData || !hasData) {
      return null;
    }

    return (
      <div className="creator-performance-by-channel__report">
        <TikTokVideoReporting
          hideTitle
          hideCreators
          hideInfo={hideInfo}
          campaign={campaign}
          tiktokVideoReporting={creatorPerformance.typedReports?.tiktokVideo}
          isLoading={false}
          audienceData={{
            isLoading: reportingAudienceData.isLoading,
            data: reportingAudienceData.audienceData?.tiktokVideo
          }}
          hasDefinedAttribution={!creatorPerformance.isBudgetSplitOutdated}
          influencersMapById={influencersMapById}
        />
      </div>
    );
  }

  renderReport(): React.ReactNode {
    const { creator, isSCP } = this.props;
    const { filterByPostType } = this.state;
    const influencersMapById: ReportingSummaryInfluencersMap = {
      [creator.id]: creator
    };

    if (filterByPostType === DELIVERABLE_TYPE_INSTAGRAM_POST) {
      return this.renderInstagramPosts(influencersMapById);
    }

    if (filterByPostType === DELIVERABLE_TYPE_INSTAGRAM_STORY) {
      return this.renderInstagramStories(influencersMapById);
    }

    if (filterByPostType === DELIVERABLE_TYPE_INSTAGRAM_REEL) {
      return this.renderInstagramReels(influencersMapById);
    }

    if (filterByPostType === DELIVERABLE_TYPE_TIKTOK_VIDEO) {
      return this.renderTiktokVideos(influencersMapById);
    }

    if (filterByPostType === DELIVERABLE_TYPE_ZALANDO_OUTFIT && isSCP) {
      return this.renderZalandoOutfit();
    }

    return null;
  }

  render(): React.ReactNode {
    const { creator, creatorPerformance, isLoading } = this.props;

    if (isNil(creator) || isEmpty(creatorPerformance.summary)) {
      return null;
    }

    return (
      <div className="creator-performance-by-channel">
        {
          isLoading
            ? this.renderSpinner()
            : (
              <React.Fragment>
                {this.renderHeader()}
                {this.renderReport()}
              </React.Fragment>
            )
        }
      </div>
    );
  }
}

const mapStateToProps = ({ account, reportingAudienceData, runtimeConfig }): StateProps => ({
  reportingAudienceData,
  isSCP: isInfluencerRealm(account.roles)
    && runtimeConfig.config.zalandoOutfitTaskTypeEnabled
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchReportingAudienceData: (
    campaignId: number,
    creatorId: number,
    filter: ReportingDeliverableTypes
  ) => (
    dispatch(fetchReportingAudienceData(campaignId, creatorId, filter))
  ),
  dispatchResetReportingAudienceData: () => dispatch(resetReportingAudienceData())
});

export default connect(mapStateToProps, mapDispatchToProps)(CreatorPerformanceByChannel);
